import React, { useState, useCallback } from "react";
import {
  Box,
  Flex,
  Text,
  TextArea,
  Button,
  Checkbox,
  Callout,
  Spinner,
} from "@radix-ui/themes";
import { apiClient } from "~/services/api-client";
import { getActiveTabContent } from "~/utils/explain-utils";
import SecurityExplainContent from "~/components/security-explain-content";

/**
 * Normalize rewrittenContent into a markdown string for SecurityExplainContent.
 * - plain string: pass through
 * - single key with array value: format as bullet list
 * - multiple keys: JSON stringify (future improvement)
 */
const normalizeRewrittenContent = (content: unknown): string => {
  if (typeof content === "string") return content;
  if (Array.isArray(content)) {
    return content.map((item) => `- ${String(item)}`).join("\n");
  }
  if (content && typeof content === "object") {
    const entries = Object.entries(content as Record<string, unknown>);
    if (entries.length === 1) {
      return normalizeRewrittenContent(entries[0][1]);
    }
    if (entries.length > 1 && entries.every(([, v]) => typeof v === "string")) {
      return entries
        .map(([key, value]) => `- ${key} - ${String(value)}`)
        .join("\n\n");
    }
  }
  return JSON.stringify(content, null, 2);
};

const REWRITE_PRESETS = [
  {
    label: "Bullets",
    prompt:
      "Summarize in one macro, sector, and security bullet each, and why it's important to the portfolio. Every bullet should have no more than 90 characters.",
  },
  {
    label: "Narrative",
    prompt:
      "Combine the highlights into a cohesive narrative. Connect the macro, sector, and security themes into a single flowing story that explains what happened and why it matters to the portfolio.",
  },
  {
    label: "Executive Brief",
    prompt:
      "Write a short executive brief for a portfolio manager. Lead with a single headline takeaway, then list the key implications for the portfolio. Keep it under 200 words and focused on what matters for decision-making.",
  },
];

interface RewriteChatProps {
  analysisResults: Record<string, unknown>;
  insightsTab: string;
  accessToken?: string | null;
  newsProvider?: string;
  isMobileView?: boolean;
}

export default function RewriteChat({
  analysisResults,
  insightsTab,
  accessToken,
  newsProvider,
  isMobileView = false,
}: RewriteChatProps) {
  const [rewritePrompt, setRewritePrompt] = useState(REWRITE_PRESETS[0].prompt);
  const [preserveHyperlinks, setPreserveHyperlinks] = useState(false);
  const [isRewriting, setIsRewriting] = useState(false);
  const [rewrittenContent, setRewrittenContent] = useState<unknown>(null);
  const [rewriteError, setRewriteError] = useState<string | null>(null);

  const handleRewrite = useCallback(async () => {
    if (!analysisResults || !rewritePrompt.trim()) return;

    setIsRewriting(true);
    setRewriteError(null);
    try {
      // insightsTab is currently hardcoded to "rewrite", which isn't a real key
      // in analysisResults — so getActiveTabContent returns null and we fall
      // through to sending the entire analysis result. This is intentional for
      // now; we may narrow the scope to a specific tab's content later.
      const tabContent = insightsTab
        ? getActiveTabContent(analysisResults, insightsTab)
        : null;
      const content = tabContent ?? JSON.stringify(analysisResults);

      const response = await apiClient.rewrite({
        content,
        prompt: rewritePrompt.trim(),
        preserveHyperlinks,
        jwtToken: accessToken || undefined,
      });

      if (response.error) {
        setRewriteError(response.error);
      } else if (response.data) {
        setRewrittenContent(response.data.rewritten_content);
      }
    } catch (error) {
      setRewriteError(
        error instanceof Error ? error.message : "Rewrite failed"
      );
    } finally {
      setIsRewriting(false);
    }
  }, [
    analysisResults,
    insightsTab,
    rewritePrompt,
    preserveHyperlinks,
    accessToken,
  ]);

  return (
    <Flex direction="column" gap="4">
      <Flex direction="column" gap="2">
        <Text size="2" weight="medium">
          Select a prompt below or write your own
        </Text>
        <Flex gap="2" wrap="wrap">
          {REWRITE_PRESETS.map((preset) => (
            <Button
              key={preset.label}
              size="1"
              variant={rewritePrompt === preset.prompt ? "solid" : "outline"}
              onClick={() => setRewritePrompt(preset.prompt)}
            >
              {preset.label}
            </Button>
          ))}
        </Flex>
        <TextArea
          placeholder="Enter rewrite instructions..."
          value={rewritePrompt}
          onChange={(e) => setRewritePrompt(e.target.value)}
          rows={4}
          size="2"
          style={{ width: "100%" }}
        />
        <Flex align="center" justify="between">
          <Text as="label" size="2">
            <Flex gap="2" align="center">
              <Checkbox
                checked={preserveHyperlinks}
                onCheckedChange={(checked) =>
                  setPreserveHyperlinks(checked === true)
                }
              />
              Preserve Hyperlinks
            </Flex>
          </Text>
          <Button
            size="2"
            onClick={handleRewrite}
            disabled={isRewriting || !rewritePrompt.trim()}
            className="demo-primary-button"
          >
            {isRewriting ? "Rewriting..." : "Submit Rewrite"}
          </Button>
        </Flex>
      </Flex>

      {rewriteError && (
        <Callout.Root color="red" size="1">
          <Callout.Text>{rewriteError}</Callout.Text>
        </Callout.Root>
      )}

      {isRewriting && (
        <Flex direction="column" align="center" justify="center" gap="3" py="6">
          <Spinner size="3" />
          <Text size="3" color="gray">
            Rewriting insights...
          </Text>
        </Flex>
      )}

      {rewrittenContent != null && !isRewriting && (
        <Box>
          <Text size="3" weight="bold" mb="3">
            Rewritten Insights
          </Text>
          <SecurityExplainContent
            content={normalizeRewrittenContent(rewrittenContent)}
            popoutNews={false}
            newsProvider={newsProvider}
            isMobileView={isMobileView}
            enableCopy
          />
        </Box>
      )}
    </Flex>
  );
}
